import {
    Box,
    Button,
    Flex,
    FormControl,
    Heading,
    HStack,
    Text,
    useToast,
} from "@chakra-ui/react";

import AppInput from "components/AppInput";
import AppSelect from "components/AppSelect";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Store } from "store";
import { showToast, stringifyErrors, toastTypes } from "utils";
import * as Yup from "yup";

export default function ManageTaxAssessmentPage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const [subArea, setSubArea] = useState("");
  const [showAmount, setShowAmount] = useState(false)
  const [keyword,SetKeyword] = useState("")
  const { dispatch, state:{user} } = useContext(Store);
  const {
    getTaxAssessment,
    addTaxAssessment,
    updateTaxAssessment,
    getTaxPayers,
    getTaxNatures,
  } = useApi();
  const {
    data: taxAssessment,
    isLoading,
    mutate,
  } = useCache("taxAssessment", () => getTaxAssessment(+id || 0));
  const {
    data: taxPayers,
    isLoading: isTaxPayerLoading,
    mutate: mutateTaxPayer,
  } = useCache("getTaxPayers", () => getTaxPayers(1, keyword));
  const {
    data: taxNatures,
    isLoading: isTaxNatureLoading,
    mutate: mutateTaxNature,
  } = useCache("getTaxNatures", () => getTaxNatures(1, "", user?.user?.institution_id||"", subArea));

  const initialValues = useMemo(() => {
    return {
      taxPayer: taxAssessment?.tax_payer_id || "",
      taxNature: taxAssessment?.tax_nature_id || "",
      amount: taxAssessment?.amount || "",
      rate: 0,
      submit: null,
    };
  }, [taxAssessment]);

  const validationSchema = Yup.object().shape({
    taxPayer: Yup.string().trim().required("Débiteur requis"),
    taxNature: Yup.string().trim().required("Nature de la Créance requis"),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let formData: any = new FormData();
        formData.append("tax_payer_id", values.taxPayer);
        formData.append("tax_nature_id", values.taxNature);
        formData.append("amount", values.amount * (values.rate/100)); // get 10 percent of the amount entered
        if (id) {
          formData.append("id", id);
        }
        let response: any = null;
        if (id) {
          response = await updateTaxAssessment(formData);
        } else {
          response = await addTaxAssessment(formData);
        }
        if (!response.error_code && !response.errors) {
          if (response.id) {
            dispatch(
              showToast({
                toast: toast,
                message: id
                  ? "Évaluation fiscale mis à jour avec succès"
                  : "Évaluation fiscale ajouté avec succès",
                status: toastTypes.success,
              })
            );
            if (id) {
              mutate(getTaxAssessment(+id));
              navigate("/tax-assessments");
            } else {
              navigate("/tax-assessments");
            }
          }
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.errors
                ? stringifyErrors(response.errors)
                : response.message,
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        console.log(ex);
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });
  return (
    <>
      {!isLoading && (
        <Flex
          gap="18px"
          bg="very_light_gray"
          flex={1}
          flexDirection="column"
          py="20px"
          px="30px"
          minHeight={"100vh"}
        >
          <Box>
            <Flex gap="4px" flexDirection="column">
              <Flex justifyContent="center" alignItems="center">
                <Heading size="text2xl" as="h4" fontFamily="Arimo">
                  {id
                    ? "Modifier la nature de règlement"
                    : "Affecter la nature de règlement"}
                </Heading>
                <Flex
                  pl="56px"
                  pr="62px"
                  gap="9px"
                  flex={1}
                  justifyContent="flex-end"
                  px={{ md: 0, base: "20px" }}
                ></Flex>
              </Flex>
            </Flex>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              flexDirection: "column",
              margin: "0px",
              alignItems: "center",
            }}
          >
            <Flex flexDirection="column" alignItems="center">
              <Flex
                gap="16px"
                bg="white"
                boxShadow="xl"
                w="100%"
                flexDirection="column"
                px={{ base: "20px", sm: "24px" }}
                py={{ base: "20px", sm: "24px" }}
                mx={{ md: "62px", base: "0px" }}
                borderRadius="16px"
              >
                <Flex gap="16px" flexDirection="column">
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Débiteur</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.taxPayer && (touched.taxPayer as boolean)
                        }
                      ></FormControl>
                      {!isTaxPayerLoading && (
                        <AppSelect
                          id="taxPayer"
                          name="taxPayer"
                          onChange={(field: string, value: any, obj:any) => {
                            setFieldValue(field, value);
                            setSubArea(obj?.sub_area_id)
                            mutateTaxNature(getTaxNatures(1, "", user?.user?.institution_id||"", obj?.sub_area_id));
                            // mutateTaxPayer(getTaxPayers(1, value));
                          }}
                          handleInputChange={(value: string) => {
                            SetKeyword(value)
                            mutateTaxPayer(getTaxPayers(1, value));
                          }}
                          value={values.taxPayer || initialValues.taxPayer}
                          placeholder="Veuillez sélectionner un Débiteur"
                          options={taxPayers}
                          isMulti={false}
                          isLoading={isTaxPayerLoading}
                          showErrorMessage={Boolean(touched.taxPayer && errors.taxPayer)}
                          errorMessage={errors.taxPayer}
                          onBlur={handleBlur}
                          filterOption={({label, data}: any, initialValues) => data.name === label || initialValues === data.phone_number }
                        />
                      )}
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Nature de la Créance</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.taxNature && (touched.taxNature as boolean)
                        }
                      ></FormControl>
                      {!isTaxNatureLoading && (
                        <AppSelect
                          id="taxNature"
                          name="taxNature"
                          onChange={(field: string, value: any, option?:any) => {
                            setFieldValue(field, value);
                            mutateTaxNature(getTaxNatures(1, value));
                            if(option?.rate > 0){
                              console.log("I am here")
                              setShowAmount(true)
                              setFieldValue("rate", option?.rate)
                            }
                          }}
                          value={values.taxNature || initialValues.taxNature}
                          placeholder="Veuillez sélectionner la nature de règlement"
                          options={taxNatures}
                          isMulti={false}
                          isLoading={isTaxNatureLoading}
                          showErrorMessage={Boolean(touched.taxNature && errors.taxNature)}
                          errorMessage={errors.taxNature}
                          onBlur={handleBlur}
                        />
                      )}
                    </Flex>
                    {showAmount && <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                      mt={1}
                    >
                      <HStack>
                        <Text color="gray_description">Montant</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.amount && (touched.amount as boolean)
                        }
                      >
                        <AppInput
                        type="number"
                          required
                          placeholder={`Montant`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="amount"
                          name="amount"
                          autoComplete="amount"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.amount}
                          showErrorMessage={Boolean(
                            touched.amount && errors.amount
                          )}
                          errorMessage={errors.amount}
                        />
                      </FormControl>
                    </Flex>}
                  </Flex>
                </Flex>
              </Flex>
              <Flex mt="24px" gap="12px">
                <Button
                  size="4xl"
                  color="gray_description"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="106px"
                  borderRadius="8px"
                  onClick={() => window.history.back()}
                >
                  Annuler
                </Button>
                <Button
                  size="4xl"
                  colorScheme="red_origin"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="132px"
                  borderRadius="8px"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Enregistrer
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      )}
    </>
  );
}
